// Enhanced security validation utilities
import DOMPurify from 'dompurify';

// Sanitize user inputs to prevent XSS
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  
  return DOMPurify.sanitize(input, {
    ALLOWED_TAGS: [], // Remove all HTML tags
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true
  });
};

// Sanitize HTML content (for rich text)
export const sanitizeHTML = (html) => {
  if (typeof html !== 'string') return html;
  
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'p', 'br', 'ul', 'ol', 'li', 'a'],
    ALLOWED_ATTR: ['href'],
    ALLOWED_URI_REGEXP: /^https?:\/\//i
  });
};

// Validate and sanitize URLs
export const sanitizeURL = (url) => {
  if (!url) return '';
  
  try {
    const cleanURL = DOMPurify.sanitize(url);
    const parsedURL = new URL(cleanURL, window.location.origin);
    
    // Only allow http/https protocols
    if (parsedURL.protocol !== 'http:' && parsedURL.protocol !== 'https:') {
      return '';
    }
    
    return parsedURL.toString();
  } catch (error) {
    return '';
  }
};

// Check for potentially dangerous patterns
export const containsDangerousPatterns = (input) => {
  if (typeof input !== 'string') return false;
  
  const dangerousPatterns = [
    /<script/i,
    /javascript:/i,
    /on\w+\s*=/i,
    /<iframe/i,
    /<object/i,
    /<embed/i,
    /eval\s*\(/i,
    /document\.cookie/i,
    /localStorage/i,
    /sessionStorage/i
  ];
  
  return dangerousPatterns.some(pattern => pattern.test(input));
};

// Enhanced email validation with additional security checks
export const isValidSecureEmail = (email) => {
  // Basic email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) return false;
  
  // Check for dangerous patterns
  if (containsDangerousPatterns(email)) return false;
  
  // Check length
  if (email.length > 254) return false;
  
  // Split local and domain parts
  const [localPart, domainPart] = email.split('@');
  
  // Validate local part length
  if (localPart.length > 64) return false;
  
  // Validate domain part
  if (domainPart.length > 253) return false;
  
  return true;
};

// Enhanced password validation with security checks
export const isValidSecurePassword = (password) => {
  // Check for dangerous patterns
  if (containsDangerousPatterns(password)) return false;
  
  // Basic password requirements
  // At least 12 characters, 1 uppercase, 1 lowercase, 1 number
  // Allow any special characters (more compatible with browser-generated passwords)
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{12,}$/;
  return passwordRegex.test(password);
};

// Validate file uploads
export const isValidFile = (file, allowedTypes, maxSize) => {
  if (!file) return false;
  
  // Check file size
  if (file.size > maxSize) return false;
  
  // Check file type
  if (allowedTypes && !allowedTypes.includes(file.type)) return false;
  
  // Check file extension
  const extension = file.name.split('.').pop().toLowerCase();
  const allowedExtensions = allowedTypes.map(type => {
    const match = type.match(/\/(.*)/);
    return match ? match[1] : '';
  });
  
  if (allowedExtensions.length > 0 && !allowedExtensions.includes(extension)) {
    return false;
  }
  
  return true;
};

// Rate limiting for inputs
export class InputRateLimiter {
  constructor(maxAttempts = 5, windowMs = 60000) {
    this.maxAttempts = maxAttempts;
    this.windowMs = windowMs;
    this.attempts = new Map();
  }
  
  isAllowed(key) {
    const now = Date.now();
    const attempts = this.attempts.get(key) || [];
    
    // Filter out old attempts
    const recentAttempts = attempts.filter(time => now - time < this.windowMs);
    
    // Check if we're under the limit
    const isAllowed = recentAttempts.length < this.maxAttempts;
    
    // Add this attempt
    recentAttempts.push(now);
    this.attempts.set(key, recentAttempts);
    
    return isAllowed;
  }
  
  reset(key) {
    this.attempts.delete(key);
  }
}

// Security-focused validation for forms
export const validateFormSecurity = (formData) => {
  const errors = {};
  
  Object.keys(formData).forEach(key => {
    const value = formData[key];
    
    // Skip if value is empty
    if (!value) return;
    
    // Check for dangerous patterns in all string fields
    if (typeof value === 'string' && containsDangerousPatterns(value)) {
      errors[key] = 'Invalid input detected';
    }
    
    // Special handling for specific fields
    if (key === 'email' && typeof value === 'string') {
      if (!isValidSecureEmail(value)) {
        errors[key] = 'Invalid email address';
      }
    }
    
    if (key === 'password' && typeof value === 'string') {
      if (!isValidSecurePassword(value)) {
        errors[key] = 'Password must be at least 12 characters with uppercase, lowercase, and number';
      }
    }
  });
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Content Security Policy validator
export const validateCSP = (cspHeader) => {
  if (!cspHeader) return false;
  
  // Check for unsafe-inline in script-src
  if (cspHeader.includes("script-src 'unsafe-inline'")) {
    console.warn('CSP contains unsafe-inline for scripts');
    return false;
  }
  
  // Check for unsafe-eval
  if (cspHeader.includes("'unsafe-eval'")) {
    console.warn('CSP contains unsafe-eval');
    return false;
  }
  
  // Check for default-src *
  if (cspHeader.includes("default-src *") || cspHeader.includes("default-src http:")) {
    console.warn('CSP has overly permissive default-src');
    return false;
  }
  
  return true;
};

export default {
  sanitizeInput,
  sanitizeHTML,
  sanitizeURL,
  containsDangerousPatterns,
  isValidSecureEmail,
  isValidSecurePassword,
  isValidFile,
  InputRateLimiter,
  validateFormSecurity,
  validateCSP
};